import { Code, ConnectError, createClient } from '@connectrpc/connect';
import {
  CadFileKind,
  ChangeType,
  VersionControlService,
} from '@puppycad/proto';
import type {
  CadNode,
  CommitInfo,
  FileInfo,
} from '@puppycad/proto';
import {
  assertImportedDocumentCanPersist,
  exportPuppyPackage,
} from '@puppycad/cad-core';
import type {
  PuppyBinaryExportResult,
} from '@puppycad/cad-core';
import { grpcWebTransport } from '@/services/api/transport/grpc-web-transport';
import {
  assertCommitTargetContext,
  createCommitFilesToBranchRequestStream,
  DEFAULT_COMMIT_CHUNK_SIZE,
  normalizeRepositoryPath,
} from '@/services/commit-files-to-branch';
import type {
  CommitFilesToBranchInvoker,
} from '@/services/commit-files-to-branch';
import type {
  CommitCurrentPartPackageInput,
  ExportPartPackageInvoker,
} from '../../../types/services';

export type {
  CommitCurrentPartPackageInput,
} from '../../../types/services';

const ASSEMBLY_METADATA_FILE_ID = '_assembly.json';

/**
 * 单个零件包提交目标。
 */
interface FileCommitTarget {
  /** 本次提交写入的仓库相对路径。 */
  readonly path: string;
  /** 文件业务 UUID；新增文件时允许为空，由服务端生成。 */
  readonly fileUuid: string;
  /** 本次文件变更类型。 */
  readonly changeType: ChangeType;
}

/**
 * 提交当前零件包后的结果。
 */
export interface CommitCurrentPartPackageResult {
  /** 后端返回的新提交信息。 */
  readonly commit: CommitInfo;
  /** 本次写入的仓库内文件路径。 */
  readonly packagePath: string;
  /** 本次提交的包字节数。 */
  readonly byteLength: number;
}

/**
 * 创建零件包提交服务的可选依赖。
 */
export interface CreatePartPackageCommitServiceOptions {
  /** 流式提交函数；测试可注入 mock，生产默认使用 VersionControlService。 */
  readonly commitFilesToBranch?: CommitFilesToBranchInvoker;
  /** `.puppy` 打包函数；测试可注入固定字节，生产默认使用 cad-core。 */
  readonly exportPartPackage?: ExportPartPackageInvoker;
  /** 提交 chunk 大小；默认 1MB。 */
  readonly chunkSize?: number;
}

/**
 * 零件包提交服务。
 */
export interface PartPackageCommitService {
  /**
   * 提交当前零件根节点包。
   *
   * @param input 提交输入。
   * @returns 后端创建的新提交信息和本次覆盖文件信息。
   */
  commitCurrentPartPackage(input: CommitCurrentPartPackageInput): Promise<CommitCurrentPartPackageResult>;
}

/**
 * 判断文件是否是 `_assembly.json` 元数据文件。
 *
 * @param file 待判断的 FileInfo。
 * @returns 是装配元数据文件时返回 true。
 */
function isAssemblyMetadataFile(file: FileInfo): boolean {
  return normalizeRepositoryPath(file.fileId) === ASSEMBLY_METADATA_FILE_ID;
}

/**
 * 为零件根节点选择已有的包文件。
 *
 * 设计说明：
 * 1. 零件提交的内容始终来自当前场景导出的离线包；
 * 2. 这里仅用于判断是否覆盖已有文件，不再把缺少旧包文件当作提交前置失败；
 * 3. 存在多个候选时优先 `.puppy`，否则沿用第一个候选，避免把路径选择散落到页面层；
 * 4. 修改提交需要 `fileUuid`，因此这里保留原始 FileInfo 供后续填充。
 *
 * @param node OpenProject 当前根节点。
 * @returns 已有零件包文件；没有旧包文件时返回 null。
 */
export function resolvePartPackageFile(node: CadNode): FileInfo | null {
  const packageFiles = node.files.filter((file) => {
    const path = normalizeRepositoryPath(file.fileId);
    return path.length > 0 && !isAssemblyMetadataFile(file);
  });
  const puppyFile = packageFiles.find((file) => normalizeRepositoryPath(file.fileId).toLowerCase().endsWith('.puppy'));
  if (puppyFile) {
    return puppyFile;
  }
  if (packageFiles.length === 1) {
    return packageFiles[0];
  }
  if (packageFiles.length <= 0) {
    return null;
  }
  throw new Error(`当前零件节点存在多个包文件但没有 .puppy 文件，无法确定覆盖目标：${packageFiles
    .map((file) => normalizeRepositoryPath(file.fileId))
    .join(', ')}`);
}

/**
 * 解析零件离线包导出的基础文件名。
 *
 * @param node OpenProject 当前根零件节点。
 * @param existingPackageFile 已有包文件；没有时传 null。
 * @returns 传入 cad-core 离线包导出的基础名称。
 */
function resolvePartPackageExportBaseName(node: CadNode, existingPackageFile: FileInfo | null): string {
  const instanceName = node.instanceName.trim();
  if (instanceName) {
    return instanceName;
  }

  const existingPath = existingPackageFile ? normalizeRepositoryPath(existingPackageFile.fileId) : '';
  if (existingPath) {
    return existingPath.replace(/\.puppy$/iu, '');
  }

  const repositoryIdText = node.repositoryId > 0n ? node.repositoryId.toString() : '';
  return repositoryIdText ? `part-${repositoryIdText}` : 'part';
}

/**
 * 从已有文件和导出结果解析零件提交目标。
 *
 * @param existingPackageFile 当前版本已有的包文件。
 * @param exportResult 本次新导出的离线包。
 * @returns 可直接写入提交流的文件目标。
 */
function resolvePartPackageCommitTarget(
  existingPackageFile: FileInfo | null,
  exportResult: PuppyBinaryExportResult,
): FileCommitTarget {
  if (existingPackageFile) {
    const packagePath = normalizeRepositoryPath(existingPackageFile.fileId);
    if (!existingPackageFile.uuid.trim()) {
      throw new Error(`零件包文件缺少 fileUuid，无法覆盖提交：${packagePath}`);
    }

    return {
      path: packagePath,
      fileUuid: existingPackageFile.uuid,
      changeType: ChangeType.MODIFIED,
    };
  }

  const packagePath = normalizeRepositoryPath(exportResult.fileName);
  if (!packagePath) {
    throw new Error('导出的零件离线包文件名为空，无法生成版本提交');
  }

  return {
    path: packagePath,
    fileUuid: '',
    changeType: ChangeType.ADDED,
  };
}

/**
 * 校验零件包提交是否仍处于有效生命周期。
 *
 * 设计说明：零件提交同样需要先导出 `.puppy` 再上传，取消后应尽早停止后续步骤，
 * 避免页面卸载后继续占用 Worker、内存与网络连接。
 *
 * @param signal 调用方传入的取消信号。
 */
function assertPartPackageCommitSignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ConnectError('零件提交请求已取消', Code.Canceled);
  }
}

/**
 * 创建零件包提交服务。
 *
 * @param options 可选依赖注入。
 * @returns 零件包提交服务。
 */
export function createPartPackageCommitService(
  options: CreatePartPackageCommitServiceOptions = {},
): PartPackageCommitService {
  const commitFilesToBranch = options.commitFilesToBranch
    ?? ((stream, invokeOptions) => createClient(VersionControlService, grpcWebTransport)
      .commitFilesToBranch(stream, invokeOptions?.signal ? { signal: invokeOptions.signal } : undefined));
  const exportPartPackage = options.exportPartPackage
    ?? ((input) => exportPuppyPackage({
      fileBaseName: input.fileBaseName,
      documentType: 'part',
      sceneRoot: input.sceneRoot,
      ...(input.workerManager ? { workerManager: input.workerManager } : {}),
      ...(input.datumExtension ? { datumExtension: input.datumExtension } : {}),
      //------ 草图离线包定义持久化 开始------
      ...(input.sketchExtensionEntries ? { sketchExtensionEntries: input.sketchExtensionEntries } : {}),
      ...(input.sketchEntityReferenceRecords
        ? { sketchEntityReferenceRecords: input.sketchEntityReferenceRecords }
        : {}),
      //------ 草图离线包定义持久化 结束------
      //------ 特征离线包定义持久化 开始------
      ...(input.featureExtensionInput ? { featureExtensionInput: input.featureExtensionInput } : {}),
      //------ 特征离线包定义持久化 结束------
      ...(input.sectionViewSnapshot ? { sectionViewSnapshot: input.sectionViewSnapshot } : {}),
    }));
  const chunkSize = options.chunkSize ?? DEFAULT_COMMIT_CHUNK_SIZE;

  return {
    async commitCurrentPartPackage(input: CommitCurrentPartPackageInput): Promise<CommitCurrentPartPackageResult> {
      const message = input.message.trim();
      if (!message) {
        throw new Error('提交说明不能为空');
      }
      if (input.rootNode.type !== CadFileKind.PART) {
        throw new Error('当前根节点不是零件，无法提交零件包');
      }
      assertCommitTargetContext(input.rootNode, '零件');
      assertImportedDocumentCanPersist(input.projectModelLoadResult);

      const packageFile = resolvePartPackageFile(input.rootNode);
      assertPartPackageCommitSignalActive(input.signal);
      const exportResult = await exportPartPackage({
        fileBaseName: resolvePartPackageExportBaseName(input.rootNode, packageFile),
        sceneRoot: input.sceneRoot,
        ...(input.workerManager ? { workerManager: input.workerManager } : {}),
        ...(input.datumExtension ? { datumExtension: input.datumExtension } : {}),
        //------ 草图离线包定义持久化 开始------
        ...(input.sketchExtensionEntries ? { sketchExtensionEntries: input.sketchExtensionEntries } : {}),
        ...(input.sketchEntityReferenceRecords
          ? { sketchEntityReferenceRecords: input.sketchEntityReferenceRecords }
          : {}),
        //------ 草图离线包定义持久化 结束------
        //------ 特征离线包定义持久化 开始------
        ...(input.featureExtensionInput ? { featureExtensionInput: input.featureExtensionInput } : {}),
        //------ 特征离线包定义持久化 结束------
        ...(input.sectionViewSnapshot ? { sectionViewSnapshot: input.sectionViewSnapshot } : {}),
      });
      assertPartPackageCommitSignalActive(input.signal);
      const packageTarget = resolvePartPackageCommitTarget(packageFile, exportResult);
      const commit = await commitFilesToBranch(createCommitFilesToBranchRequestStream({
        repositoryId: input.rootNode.repositoryId,
        branchId: input.rootNode.branchId,
        message,
        baseCommitId: input.rootNode.commitId,
        chunkSize,
        signal: input.signal,
        files: [{
          path: packageTarget.path,
          fileUuid: packageTarget.fileUuid,
          changeType: packageTarget.changeType,
          bytes: exportResult.bytes,
        }],
      }), { signal: input.signal });

      return {
        commit,
        packagePath: packageTarget.path,
        byteLength: exportResult.bytes.byteLength,
      };
    },
  };
}
